import mixpanel, { Mixpanel } from 'mixpanel-browser';
import { CURRENT_STEP, USER_EMAIL, USER_NAME } from './constants';

type TrackEventParams = {
  eventName: string;
  eventProperties: { [key: string]: string };
};

type UserProperties = {
  [key: string]: string;
};

type UserPropertiesNumeric = {
  [key: string]: number;
};

export const projectToken =
  process.env.REACT_APP_SALES_WEB_MIXPANEL_PROJECT_TOKEN;

/**
 * Singleton structure is used in order to ensure one instance of
 * mixpanel that will be the global entry point for
 * all analytics-related operations.
 *
 * Note: We communicate with the singleton through the exported
 * functions.
 */
export default class MixpanelAnalytics {
  private static instance: MixpanelAnalytics;

  private logIn: boolean; // flag for user is logged in or not

  public analytics: Mixpanel;

  private userName: string;

  private userEmail: string;

  private constructor() {
    mixpanel.init(projectToken as string);
    this.analytics = mixpanel;
    this.logIn = false;
    this.userName = '';
    this.userEmail = '';
  }

  /** Accesors */
  public static getInstance(): MixpanelAnalytics {
    if (!MixpanelAnalytics.instance) {
      MixpanelAnalytics.instance = new MixpanelAnalytics();
    }
    return MixpanelAnalytics.instance;
  }

  get isLoggedIn(): boolean {
    return this.logIn;
  }

  set isLoggedIn(status: boolean) {
    this.logIn = status;
  }

  /** Identifies a anonymous user with given id, equivalent to a "login" action */
  public login(id: string, name: string, email: string): void {
    if (['', null, undefined].includes(id)) {
      return;
    }
    this.isLoggedIn = true;
    this.analytics.identify(id);
    this.userName = name;
    this.userEmail = email;
  }

  /**
   * From the docs:
   * In order to handle multiple identities on the same device, you'll want to call mixpanel.reset() on log out.
   * This resets the distinct_id and removes existing super properties. Mixpanel will treat subsequent events
   * on the same device as performed by a new user.
   *
   * further read: https://help.mixpanel.com/hc/en-us/articles/115004497803#multiple-users-on-single-devices-identify-on-login-reset-on-logout
   */
  public logout(): void {
    this.isLoggedIn = false;
    this.userName = '';
    this.userEmail = '';
    this.analytics.reset();
  }

  /**
   * Announces the user's new identity to mixpanel. This ties the uniqueid that mixpanel
   * assigns to a browser and links it with the given id. Effectively works as a "sign up" action.
   * Should only ever be called once per user.
   */
  public signup(id: string): void {
    if (['', null, undefined].includes(id)) {
      return;
    }
    this.analytics.alias(id);
    this.isLoggedIn = true;
  }

  /** Sets user properties */
  public setUserProperties(properties: UserProperties): void {
    // make sure a user has been logged in
    if (!this.isLoggedIn) {
      return;
    }
    this.analytics.people.set(properties);
  }

  /** Sets user properties once */
  public setUserPropertiesOnce(properties: UserProperties): void {
    // make sure a user has been logged in
    if (!this.isLoggedIn) {
      return;
    }
    this.analytics.people.set_once(properties);
  }

  /**
   * Increment/decrement numeric people property
   * @param properties If given a string, increments/decrements the property name by 1.
   *                   If given a dictionary of property names and values, increments/decrements all corresponding
   *                   property names by its respective value
   */
  public incrementUserProperty(
    properties: string | UserPropertiesNumeric,
  ): void {
    // make sure user has been logged in
    if (!this.isLoggedIn) {
      return;
    }
    this.analytics.people.increment(properties);
  }

  /** Tracks event with specified eventName and eventProperties */
  public trackEvent({ eventName, eventProperties }: TrackEventParams): void {
    this.analytics.track(eventName, {
      ...eventProperties,
      [USER_EMAIL]: this.userEmail,
      [USER_NAME]: this.userName,
    });
  }

  /** */
  public disableAllTracking(): void {
    this.analytics.disable();
  }
}

/** Bridge Functions */
export function login(id: string, name: string, email: string): void {
  MixpanelAnalytics.getInstance().login(id, name, email);
}

export function logout(): void {
  MixpanelAnalytics.getInstance().logout();
}

export function signup(id: string): void {
  MixpanelAnalytics.getInstance().signup(id);
}

export function setUserProperties(params: UserProperties): void {
  MixpanelAnalytics.getInstance().setUserProperties(params);
}

export function setUserPropertiesOnce(params: UserProperties): void {
  MixpanelAnalytics.getInstance().setUserPropertiesOnce(params);
}

export function incrementUserProperty(
  params: string | UserPropertiesNumeric,
): void {
  MixpanelAnalytics.getInstance().incrementUserProperty(params);
}

export function trackEvent(params: TrackEventParams): void {
  MixpanelAnalytics.getInstance().trackEvent(params);
}

export function trackCurrentStep(eventName: string, date: string): void {
  MixpanelAnalytics.getInstance().setUserProperties({
    [CURRENT_STEP]: eventName,
  });

  MixpanelAnalytics.getInstance().trackEvent({
    eventName,
    eventProperties: {
      date,
    },
  });
}

export function disableAllTracking(): void {
  MixpanelAnalytics.getInstance().disableAllTracking();
}
